import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { sendEmail } from '../emailService'
import { sendWhatsApp } from '../whatsappClient'
import { createMeetingLink, createJitsiLink } from '../meetHelper'
import { checkTeacherAvailability, DEFAULT_DURATION_MINUTES } from '../availabilityHelper'

// ── Class Reimbursements ──────────────────────────────────────────────────
// Admin flags a class that a student didn't actually get (teacher no-show,
// class cancelled last-minute, etc.) and grants a makeup class in one step.
// Deliberately no restriction on which classes qualify — "not taken" is an
// admin judgment call, not something derivable from is_cancelled/attendance
// (a class can be marked cancelled for reasons that don't warrant a makeup,
// and one that looks fine on paper might still not have happened).

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short', year:'numeric' })
}
function fmtTime(t) {
  if (!t) return '—'
  return new Date('2000-01-01T' + t).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })
}
function fmtDateTime(d, t) {
  if (!d) return '—'
  return `${fmtDate(d)}${t ? ' at ' + fmtTime(t) : ''}`
}

export default function ClassReimbursements({ profile }) {
  const [students,     setStudents]     = useState([])
  const [teachers,     setTeachers]     = useState([])
  const [selStudent,   setSelStudent]   = useState('')
  const [classes,      setClasses]      = useState([])   // this student's classes, with course_id attached
  const [loadingCls,   setLoadingCls]   = useState(false)

  const [openClassId,  setOpenClassId]  = useState(null) // which class's reimburse form is expanded
  const [reason,       setReason]       = useState('')
  const [notes,        setNotes]        = useState('')
  const [newDate,      setNewDate]      = useState('')
  const [newTime,      setNewTime]      = useState('')
  const [teacherId,    setTeacherId]    = useState('')
  const [duration,     setDuration]     = useState(DEFAULT_DURATION_MINUTES)
  const [autoZoom,     setAutoZoom]     = useState(true)
  const [sendMail,     setSendMail]     = useState(true)
  const [availWarning, setAvailWarning] = useState('')
  const [confirmedOverride, setConfirmedOverride] = useState(false)
  const [busy,         setBusy]         = useState(false)
  const [err,          setErr]          = useState('')
  const [ok,           setOk]           = useState('')

  const [history,      setHistory]      = useState([])
  const [loadingHist,  setLoadingHist]  = useState(true)

  useEffect(() => { loadStudents(); loadTeachers(); loadHistory() }, [])
  useEffect(() => { if (selStudent) loadClasses(); else setClasses([]) }, [selStudent]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setAvailWarning(''); setConfirmedOverride(false) }, [teacherId, newDate, newTime])

  async function loadStudents() {
    const { data } = await supabase.from('profiles').select('id,full_name,email,phone,timezone').eq('role','student').order('full_name')
    setStudents(data || [])
  }
  async function loadTeachers() {
    const { data } = await supabase.from('profiles').select('id,full_name,email,phone').eq('role','teacher').order('full_name')
    setTeachers(data || [])
  }

  async function loadClasses() {
    setLoadingCls(true); setErr(''); setOpenClassId(null)
    const { data: enrols } = await supabase.from('enrollments').select('class_id,course_id').eq('student_id', selStudent)
    const classIds = (enrols || []).map(e => e.class_id).filter(Boolean)
    if (!classIds.length) { setClasses([]); setLoadingCls(false); return }
    const courseByClass = {}
    for (const e of enrols) courseByClass[e.class_id] = e.course_id

    const { data: cls, error } = await supabase.from('classes').select('*').in('id', classIds)
      .order('class_date', { ascending: false }).order('start_time', { ascending: false })
    if (error) { setErr(error.message); setLoadingCls(false); return }
    setClasses((cls || []).map(c => ({ ...c, course_id: courseByClass[c.id] || null })))
    setLoadingCls(false)
  }

  async function loadHistory() {
    setLoadingHist(true)
    const { data } = await supabase.from('class_reimbursements')
      .select('*, student:student_id(full_name), original:original_class_id(title,class_date,start_time), makeup:makeup_class_id(title,class_date,start_time,meet_link)')
      .order('created_at', { ascending: false }).limit(100)
    setHistory(data || [])
    setLoadingHist(false)
  }

  function openReimburse(cls) {
    setOpenClassId(cls.id)
    setReason(''); setNotes(''); setNewDate(''); setNewTime('')
    setTeacherId(cls.teacher_id || '')
    setDuration(cls.duration_minutes || DEFAULT_DURATION_MINUTES)
    setAutoZoom(true); setSendMail(true)
    setErr(''); setOk('')
  }

  const submit = useCallback(async (cls, overriding = false) => {
    setErr('')
    if (!newDate) return setErr('Pick a date for the makeup class.')
    if (!reason.trim()) return setErr('Give a reason this class wasn\'t taken.')

    if (teacherId && newTime && !confirmedOverride && !overriding) {
      const check = await checkTeacherAvailability({ teacherId, date: newDate, startTime: newTime, durationMinutes: duration })
      if (check.conflicts.length > 0) return setErr(`⛔ ${check.reason}`)
      if (!check.withinAvailability) { setAvailWarning(check.reason); return }
    }
    if (overriding) setConfirmedOverride(true)

    setBusy(true)
    const student = students.find(s => s.id === selStudent)
    const teacher = teachers.find(t => t.id === teacherId)

    try {
      // Meet link — same fallback chain ScheduleClasses.js uses: real Zoom
      // meeting if possible, Jitsi if Zoom fails or wasn't requested.
      let meetLink = null
      if (autoZoom && newTime) {
        const result = await createMeetingLink(`${cls.title} (Makeup)`, newDate, newTime, duration)
        meetLink = result?.url || createJitsiLink(`${cls.title} Makeup`, newDate, newTime)
      } else {
        meetLink = createJitsiLink(`${cls.title} Makeup`, newDate, newTime)
      }

      const { data: newClass, error: clsErr } = await supabase.from('classes').insert([{
        title:            `${cls.title} (Makeup)`,
        teacher_id:       teacherId || null,
        teacher_name:     teacher?.full_name || cls.teacher_name || null,
        class_date:       newDate,
        start_time:       newTime || null,
        duration_minutes: duration,
        batch:            cls.batch || null,
        meet_link:        meetLink,
        created_by:       profile.full_name,
      }]).select().single()
      if (clsErr) throw new Error(clsErr.message)

      const { error: enrollErr } = await supabase.from('enrollments').insert({
        student_id: selStudent, class_id: newClass.id, course_id: cls.course_id || null,
      })
      if (enrollErr) throw new Error(`Makeup class was created, but enrolling the student failed: ${enrollErr.message}`)

      const d = new Date(newDate + 'T00:00:00')
      await supabase.from('events').insert({
        title: newClass.title, event_type: 'class',
        day: d.getDate(), month: d.getMonth()+1, year: d.getFullYear(),
        time: newTime || null, teacher_name: teacher?.full_name || null,
        batch: cls.batch || null, class_id: newClass.id,
      })

      const { error: reimburseErr } = await supabase.from('class_reimbursements').insert({
        student_id:        selStudent,
        original_class_id: cls.id,
        makeup_class_id:    newClass.id,
        reason:             reason.trim(),
        notes:              notes.trim() || null,
        created_by:         profile.full_name,
        created_by_id:      profile.id,
      })
      if (reimburseErr) throw new Error(`Makeup class was created, but logging the reimbursement failed: ${reimburseErr.message}`)

      // Notify student — reuses class_rescheduled_wa/'class_scheduled' email
      // rather than a new template: "your class moved from X to Y" reads
      // fine for "here's the makeup for the one you missed".
      if (sendMail && student?.email) {
        sendEmail('class_scheduled', student.email, {
          name: student.full_name, classTitle: `${cls.title} — Makeup Class`,
          classDate: fmtDate(newDate), startTime: newTime || null,
          teacherName: teacher?.full_name || null, zoomLink: meetLink, studentTZ: student.timezone,
        })
      }
      if (sendMail && student?.phone) {
        sendWhatsApp('class_rescheduled_wa', student.phone, [
          student.full_name, cls.title, fmtDateTime(cls.class_date, cls.start_time), fmtDateTime(newDate, newTime),
        ])
      }
      if (teacher?.email) {
        sendEmail('class_scheduled', teacher.email, {
          name: teacher.full_name, classTitle: `Makeup: ${cls.title} with ${student?.full_name || 'student'}`,
          classDate: fmtDate(newDate), startTime: newTime || null,
          teacherName: teacher.full_name, zoomLink: meetLink,
        })
      }
      if (teacher?.phone) {
        sendWhatsApp('class_rescheduled_wa', teacher.phone, [
          teacher.full_name, cls.title, fmtDateTime(cls.class_date, cls.start_time), fmtDateTime(newDate, newTime),
        ])
      }

      setOk(`✓ Makeup class scheduled for ${student?.full_name} on ${fmtDate(newDate)}`)
      setOpenClassId(null)
      loadHistory()
    } catch (e) {
      setErr(e.message)
    }
    setBusy(false)
  }, [newDate, newTime, reason, notes, teacherId, duration, autoZoom, sendMail, confirmedOverride, selStudent, students, teachers, profile])

  const inp = { width:'100%', background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius:'10px', padding:'9px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box' }
  const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'4px', marginTop:'10px' }

  return (
    <div style={{ marginTop:'14px' }}>
      <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'16px', maxWidth:'640px' }}>
        Flag a class a student didn't actually get (teacher no-show, cancelled last-minute, etc.) and schedule a makeup class for it in one step.
      </div>

      <label style={lbl}>Student</label>
      <select style={{ ...inp, maxWidth:'360px' }} value={selStudent} onChange={e=>setSelStudent(e.target.value)}>
        <option value="">— Select a student —</option>
        {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
      </select>

      {ok  && <div style={{ marginTop:'12px', padding:'10px 14px', background:'rgba(16,185,129,0.1)', color:'#34d399', borderRadius:'10px', fontSize:'13px' }}>{ok}</div>}
      {err && !openClassId && <div style={{ marginTop:'12px', padding:'10px 14px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'10px', fontSize:'13px' }}>{err}</div>}

      {selStudent && (
        <div style={{ marginTop:'18px' }}>
          {loadingCls ? (
            <div style={{ padding:'2rem', textAlign:'center', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading classes...</div>
          ) : classes.length === 0 ? (
            <div style={{ padding:'2rem', textAlign:'center', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>No classes found for this student.</div>
          ) : (
            <div style={{ display:'grid', gap:'8px' }}>
              {classes.map(cls => (
                <div key={cls.id} style={{ background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'12px', padding:'14px 16px' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
                    <div>
                      <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))' }}>
                        {cls.title} {cls.is_cancelled && <span style={{ color:'#f87171', fontWeight:700, fontSize:'11px' }}>· Cancelled</span>}
                      </div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>
                        📅 {fmtDateTime(cls.class_date, cls.start_time)} {cls.teacher_name && `· 👤 ${cls.teacher_name}`}
                      </div>
                    </div>
                    <button onClick={() => openClassId === cls.id ? setOpenClassId(null) : openReimburse(cls)}
                      style={{ fontSize:'12px', fontWeight:700, padding:'8px 14px', borderRadius:'9px', background: openClassId===cls.id ? 'rgba(var(--fg-rgb),0.08)' : 'rgba(139,92,246,0.15)', color: openClassId===cls.id ? 'rgba(var(--fg-rgb),var(--a50))' : '#a78bfa', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
                      {openClassId === cls.id ? '✕ Cancel' : '↩ Reimburse'}
                    </button>
                  </div>

                  {openClassId === cls.id && (
                    <div style={{ marginTop:'14px', paddingTop:'14px', borderTop:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
                      <label style={lbl}>Reason this class wasn't taken *</label>
                      <textarea style={{ ...inp, minHeight:'60px', resize:'vertical' }} value={reason}
                        onChange={e=>setReason(e.target.value)} placeholder="e.g. Teacher didn't join, connectivity issue, class cancelled last-minute..." />

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                        <div>
                          <label style={lbl}>Makeup Date *</label>
                          <input style={inp} type="date" value={newDate} onChange={e=>setNewDate(e.target.value)}
                            min={new Date().toISOString().slice(0,10)} />
                        </div>
                        <div>
                          <label style={lbl}>Makeup Time</label>
                          <input style={inp} type="time" value={newTime} onChange={e=>setNewTime(e.target.value)} />
                        </div>
                      </div>

                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                        <div>
                          <label style={lbl}>Teacher</label>
                          <select style={inp} value={teacherId} onChange={e=>setTeacherId(e.target.value)}>
                            <option value="">— No teacher —</option>
                            {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={lbl}>Duration (minutes)</label>
                          <input style={inp} type="number" min="15" step="15" value={duration} onChange={e=>setDuration(Number(e.target.value))} />
                        </div>
                      </div>

                      <label style={lbl}>Notes (internal, optional)</label>
                      <textarea style={{ ...inp, minHeight:'50px', resize:'vertical' }} value={notes} onChange={e=>setNotes(e.target.value)} />

                      <div style={{ display:'flex', gap:'16px', marginTop:'12px', flexWrap:'wrap' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a50))', cursor:'pointer' }}>
                          <input type="checkbox" checked={autoZoom} onChange={e=>setAutoZoom(e.target.checked)} /> Create real Zoom link
                        </label>
                        <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a50))', cursor:'pointer' }}>
                          <input type="checkbox" checked={sendMail} onChange={e=>setSendMail(e.target.checked)} /> Notify student (email + WhatsApp)
                        </label>
                      </div>

                      {availWarning && !confirmedOverride && (
                        <div style={{ marginTop:'10px', padding:'10px 14px', background:'rgba(244,163,53,0.1)', color:'#f4a335', borderRadius:'10px', fontSize:'12px' }}>
                          ⚠ {availWarning}
                          <button onClick={() => submit(cls, true)} style={{ marginLeft:'10px', fontSize:'11px', fontWeight:700, padding:'4px 10px', borderRadius:'7px', background:'rgba(244,163,53,0.2)', color:'#f4a335', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Schedule anyway</button>
                        </div>
                      )}
                      {err && <div style={{ marginTop:'10px', padding:'10px 14px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'10px', fontSize:'12px' }}>{err}</div>}

                      <button onClick={() => submit(cls)} disabled={busy}
                        style={{ marginTop:'14px', fontSize:'13px', fontWeight:700, padding:'10px 18px', borderRadius:'10px', background:'#8b5cf6', color:'#fff', border:'none', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily:'inherit' }}>
                        {busy ? 'Scheduling...' : '✓ Confirm Reimbursement'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── History ─────────────────────────────────────────────── */}
      <div style={{ marginTop:'32px' }}>
        <div style={{ fontSize:'15px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))', marginBottom:'12px' }}>📋 Reimbursement History</div>
        {loadingHist ? (
          <div style={{ padding:'2rem', textAlign:'center', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>
        ) : history.length === 0 ? (
          <div style={{ padding:'2rem', textAlign:'center', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>No reimbursements logged yet.</div>
        ) : (
          <div style={{ background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', overflow:'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1.3fr 1.3fr 1.5fr 110px', padding:'10px 16px', background:'rgba(var(--fg-rgb),0.04)', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
              {['Student','Missed Class','Makeup Class','Reason','Logged'].map(h => (
                <div key={h} style={{ fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.08em', textTransform:'uppercase' }}>{h}</div>
              ))}
            </div>
            {history.map((r, i) => (
              <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 1.3fr 1.3fr 1.5fr 110px', padding:'10px 16px', borderBottom: i < history.length-1 ? '0.5px solid rgba(var(--fg-rgb),0.04)' : 'none', alignItems:'center' }}>
                <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a70))' }}>{r.student?.full_name || '—'}</div>
                <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>{r.original?.title || '—'}<br/>{fmtDateTime(r.original?.class_date, r.original?.start_time)}</div>
                <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>{r.makeup ? <>{r.makeup.title}<br/>{fmtDateTime(r.makeup.class_date, r.makeup.start_time)}</> : '—'}</div>
                <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>{r.reason}</div>
                <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))' }}>{fmtDate(r.created_at?.slice(0,10))}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
